/**
 * usePulseBarData — polls the three pulse-bar data sources on independent
 * cadences and keeps the last-good value in state (stale-while-revalidate).
 *
 * Intervals match BRD §6.1:
 *   health    → 15 s
 *   decisions → 15 s
 *   agents    → 30 s
 */
import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { getAdminToken } from "@/lib/authStorage";

function authHeaders() {
  const token = getAdminToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function usePoll(fetcher, intervalMs, immediate = true) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const timerRef = useRef(null);

  const fetch = useCallback(async () => {
    try {
      const result = await fetcher();
      setData(result);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      setError(err);
      // keep previous data — stale-while-revalidate
    }
  }, [fetcher]);

  useEffect(() => {
    if (immediate) fetch();
    timerRef.current = setInterval(fetch, intervalMs);
    return () => clearInterval(timerRef.current);
  }, [fetch, intervalMs, immediate]);

  return { data, error, lastUpdated, refetch: fetch };
}

export function usePulseBarData() {
  const fetchHealth = useCallback(async () => {
    const res = await axios.get(`${API}/admin/ops/health`, {
      headers: authHeaders(),
    });
    return res.data;
  }, []);

  const fetchDecisions = useCallback(async () => {
    const res = await axios.get(
      `${API}/admin/ops/decisions?include_decided=false&limit=50`,
      { headers: authHeaders() }
    );
    return res.data;
  }, []);

  const fetchAgents = useCallback(async () => {
    const res = await axios.get(`${API}/admin/ops/agents`, {
      headers: authHeaders(),
    });
    return res.data;
  }, []);

  const fetchActions = useCallback(async () => {
    const res = await axios.get(`${API}/admin/ops/actions?limit=20`, {
      headers: authHeaders(),
    });
    return res.data;
  }, []);

  const { data: health, error: healthError } = usePoll(fetchHealth, 15_000);
  const { data: decisionsData } = usePoll(fetchDecisions, 15_000);
  const { data: agentsData } = usePoll(fetchAgents, 30_000);
  const { data: actionsData } = usePoll(fetchActions, 10_000);

  // Derived values
  const decisions = decisionsData?.decisions || [];
  const pendingCount = decisions.filter((d) => !d.decided_at).length;
  const maxStakes = pendingCount > 0
    ? decisions.reduce((acc, d) => {
        const rank = { high: 3, medium: 2, low: 1 };
        return Math.max(acc, rank[d.stakes] || 0);
      }, 0)
    : 0;

  const agents = agentsData?.agents || [];
  const platformAgents = agents.filter((a) => a.kind === "platform");
  const activeAgents = platformAgents.filter(
    (a) => a.status_computed === "active" || a.status_computed === "degraded"
  ).length;
  const totalAgents = platformAgents.length;
  const cost24h = agentsData?.total_24h_cost_usd || 0;

  // Check if manual override is active (any non-killed platform agent is paused with manual_override reason)
  const isManualOverrideActive = platformAgents.some(
    (a) =>
      a.status === "paused" &&
      typeof a.paused_reason === "string" &&
      a.paused_reason.startsWith("manual_override:")
  );

  return {
    health,
    healthError,
    pendingDecisions: pendingCount,
    maxStakes,
    activeAgents,
    totalAgents,
    cost24h,
    recentActions: actionsData?.actions || [],
    isManualOverrideActive,
    rawAgents: agents,
  };
}
